<template>
  <div id="main" class="airdrop-container">
    <div id="manu">
      <navigation />
    </div>
    <div id="contentBox" class="content-box">
      <div class="registervideoContainer"></div>
      <div class="logo-container">
        <img src="@/assets/icons/airdrop-icon.png" alt="AirDrop Logo" class="logo">
        <h1 class="title">微盘快传</h1>
      </div>
      <div class="transfer-controls">
        <!-- 设备扫描区域（自动扫描） -->
        <div class="device-discovery">
          <h3>附近设备</h3>
          <div class="bubble-container">
            <div
              v-for="device in discoveredDevices"
              :key="device.id"
              class="device-bubble"
              @click="connectDevice(device)"
              :class="{ 'disabled': connecting }"
            >
              <span class="bubble-text">{{ device.name }} ({{ device.type }})</span>
              <span v-if="connecting" class="bubble-status">连接中...</span>
            </div>
            <div
              v-if="discoveredDevices.length === 0"
              class="device-bubble placeholder"
            >
              <span class="bubble-text">暂无设备</span>
            </div>
          </div>
        </div>
        <div class="mode-selection">
          <label>
            <input type="radio" v-model="transferMode" value="sameNetwork" /> 同一网段 (Wi-Fi/蓝牙/NFC)
          </label>
          <label>
            <input type="radio" v-model="transferMode" value="loggedIn" /> 登录后传输
          </label>
          <label>
            <input type="radio" v-model="transferMode" value="anonymous" /> 未登录一次性传输
          </label>
        </div>
        <div class="protocol-selection" v-if="transferMode === 'sameNetwork'">
          <label>
            <input type="radio" v-model="transferProtocol" value="bluetooth" /> 蓝牙
          </label>
          <label>
            <input type="radio" v-model="transferProtocol" value="nfc" /> NFC
          </label>
          <label>
            <input type="radio" v-model="transferProtocol" value="wifi" /> Wi-Fi
          </label>
        </div>
        <div class="send-section">
          <input type="file" ref="fileInput" @change="selectFile" class="file-input" multiple>
          <div class="time-selection">
            <label>文件有效期（小时）:</label>
            <input type="number" v-model="fileExpiryHours" min="1" max="720" class="time-input" />
          </div>
          <button @click="showSendConfirmation" class="transfer-button" :disabled="!transferMode || !selectedFiles.length">发送文件</button>
          <span v-if="sendingStatus" class="status">{{ sendingStatus }}</span>
        </div>
        <div class="receive-section">
          <button @click="showReceiveInput" class="transfer-button" :disabled="!transferMode">开始接收</button>
          <span v-if="receivingStatus" class="status">{{ receivingStatus }}</span>
          <div v-if="receivedFiles.length" class="received-files">
            <h3>已接收文件:</h3>
            <ul>
              <li v-for="(file, index) in receivedFiles" :key="index">
                {{ file.name }} ({{ file.size }} bytes)
                <a :href="file.url" target="_blank" download>下载</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="w">
        <div v-if="loading" class="loading">加载中...</div>
        <div v-else-if="error" class="error">{{ error }}</div>
      </div>

      <!-- 发送确认弹窗 -->
      <div v-if="showSendDialog" class="modal">
        <div class="modal-content" style="height: 300px;">
          <h3>确认发送</h3>
          <p>有效期: {{ fileExpiryHours }} 小时</p>
          <p>接收链接: <a :href="receiveLink" target="_blank">{{ receiveLink }}</a></p>
          <button @click="copyLink" class="modal-button">复制链接</button>
          <button @click="copyPickupCode" class="modal-button">复制取件码</button>
          <button @click="toggleQRCode" class="modal-button">{{ showQR ? '隐藏二维码' : '显示二维码' }}</button>
          <div v-if="showQR" class="qr-code" ref="qrCodeRef">
            <canvas id="qrcode-canvas"></canvas>
            <button @click="copyQRCode" class="modal-button">复制二维码</button>
          </div>
          <button v-if="selectedDevice" @click="confirmDeviceSend" class="modal-button confirm">发送到 {{ selectedDevice.name }}</button>
          <button @click="confirmSend" class="modal-button confirm">确认发送</button>
          <button @click="closeSendDialog" class="modal-button">{{ showQR ? '返回' : '取消' }}</button>
        </div>
      </div>

      <!-- 接收输入弹窗 -->
      <div v-if="showReceiveDialog" class="modal">
        <div class="modal-content" style="height: 250px;">
          <h3>输入取件码</h3>
          <input type="text" v-model="inputPickupCode" placeholder="输入取件码" class="pickup-input" />
          <div class="time-selection">
            <label>文件保存时间（小时）:</label>
            <input type="number" v-model="verifyExpiryHours" min="1" max="720" class="time-input" />
          </div>
          <button @click="startReceiving" class="modal-button confirm">确认接收</button>
          <button @click="closeReceiveDialog" class="modal-button">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';
import QRCode from 'qrcode';
import { ElMessage } from 'element-plus';

// WebRTC 连接
let peerConnection = null;
// 轮询定时器
let pollingInterval = null;

export default {
  name: 'AirDrop',
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const transferMode = ref('loggedIn');
    const transferProtocol = ref('wifi'); // 默认传输协议
    const sendingStatus = ref('');
    const receivingStatus = ref('');
    const receivedFiles = ref([]);
    const selectedFiles = ref([]);
    const pickupCode = ref('');
    const inputPickupCode = ref('');
    const transferHistory = ref([]);
    const fileExpiryHours = ref(24);
    const verifyExpiryHours = ref(24);
    const showSendDialog = ref(false);
    const showReceiveDialog = ref(false);
    const showQR = ref(false);
    const receiveLink = ref('');
    const VersionFlag = ref(false);
    const VersionExplain = ref('');
    const currentVersion = ref('1.0');
    const versionFileUrl = ref('');
    const discoveredDevices = ref([]); // 发现的设备列表
    const connecting = ref(false); // 连接状态
    const selectedDevice = ref(null); // 选择的设备

    onMounted(() => {
      loadSettings();
      checkVersion();
      startBackgroundScan();
      const token = localStorage.getItem('token');
      const isLoggedIn = localStorage.getItem('isLoggedIn');
      if (!token || !isLoggedIn) {
        transferMode.value = 'anonymous';
      }
      // 检查平台能力
      if (!('bluetooth' in navigator)) {
        ElMessage.warning('此设备不支持蓝牙功能');
      }
      if (!('NDEFReader' in window)) {
        ElMessage.warning('此设备不支持 NFC 功能');
      }
      // 启动自动扫描（后台运行）
      startAutoScan();
    });

    onBeforeUnmount(() => {
      if (peerConnection) peerConnection.close();
      if (pollingInterval) clearInterval(pollingInterval);
    });

    const startAutoScan = () => {
      pollDevices(transferMode.value === 'sameNetwork' ? 'wifi' : 'network');
      pollingInterval = setInterval(() => {
        pollDevices(transferMode.value === 'sameNetwork' ? 'wifi' : 'network');
      }, 30000); // 每 30 秒轮询一次
    };

    const pollDevices = async (protocol) => {
      try {
        const response = await axios.post(
          'https://a.410121.xyz/api/scan',
          {
            type: 'scan',
            protocol,
            userId: localStorage.getItem('userId') || 'anonymous',
            deviceId: navigator.userAgent,
            token: localStorage.getItem('token'),
            location: 'city-name',
            permissions: ['transfer'],
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000,
          }
        );
        if (response.data.code === 200) {
          discoveredDevices.value = response.data.devices.filter(
            (device) => device.permissions.includes('transfer')
          );
        } else {
          throw new Error(response.data.message || '设备扫描失败');
        }
      } catch (err) {
        console.error('Polling error:', err); // 仅记录错误，不显示
      }
    };

    const connectDevice = async (device) => {
      connecting.value = true;
      try {
        if (device.type === 'Bluetooth') {
          await device.gatt.connect();
          selectedDevice.value = device;
          ElMessage.success('蓝牙连接成功');
        } else if (device.type === 'NFC') {
          ElMessage.info('请靠近设备进行 NFC 对碰');
          selectedDevice.value = device;
        } else {
          peerConnection = new RTCPeerConnection({
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
          });
          peerConnection.onicecandidate = (e) => {
            if (e.candidate) {
              sendIceCandidate(device.id, e.candidate);
            }
          };
          peerConnection.ondatachannel = (event) => {
            const channel = event.channel;
            channel.onmessage = (msg) => {
              console.log('WebRTC 收到:', msg.data);
              if (typeof msg.data === 'string') {
                const fileData = JSON.parse(msg.data);
                receivedFiles.value.push({
                  name: fileData.name,
                  size: fileData.size,
                  url: URL.createObjectURL(new Blob([fileData.data])),
                });
                receivingStatus.value = '文件接收成功';
              }
            };
          };
          const offer = await peerConnection.createOffer();
          await peerConnection.setLocalDescription(offer);
          await sendOffer(device.id, offer);
          selectedDevice.value = device;
          ElMessage.success('WebRTC 连接建立');
        }
        connecting.value = false;
      } catch (err) {
        connecting.value = false;
        ElMessage.error(`连接失败: ${err.message}`);
      }
    };

    const sendOffer = async (targetDeviceId, offer) => {
      try {
        await axios.post(
          'https://a.410121.xyz/api/webrtc/offer',
          {
            type: 'offer',
            offer,
            targetDeviceId,
            sourceId: localStorage.getItem('userId') || 'anonymous',
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000,
          }
        );
      } catch (err) {
        ElMessage.error(`发送 Offer 失败: ${err.message}`);
      }
    };

    const sendIceCandidate = async (targetDeviceId, candidate) => {
      try {
        await axios.post(
          'https://a.410121.xyz/api/webrtc/icecandidate',
          {
            type: 'icecandidate',
            candidate,
            targetDeviceId,
            sourceId: localStorage.getItem('userId') || 'anonymous',
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000,
          }
        );
      } catch (err) {
        ElMessage.error(`发送 ICE Candidate 失败: ${err.message}`);
      }
    };

    const handleWebRTCOffer = async (offer, sourceId) => {
      if (!peerConnection) {
        peerConnection = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        });
        peerConnection.ondatachannel = (event) => {
          const channel = event.channel;
          channel.onmessage = (msg) => {
            if (typeof msg.data === 'string') {
              const fileData = JSON.parse(msg.data);
              receivedFiles.value.push({
                name: fileData.name,
                size: fileData.size,
                url: URL.createObjectURL(new Blob([fileData.data])),
              });
              receivingStatus.value = '文件接收成功';
            }
          };
        };
        peerConnection.onicecandidate = (e) => {
          if (e.candidate) {
            sendIceCandidate(sourceId, e.candidate);
          }
        };
      }
      await peerConnection.setRemoteDescription(offer);
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      await sendAnswer(sourceId, answer);
    };

    const sendAnswer = async (targetDeviceId, answer) => {
      try {
        await axios.post(
          'https://a.410121.xyz/api/webrtc/answer',
          {
            type: 'answer',
            answer,
            targetDeviceId,
            sourceId: localStorage.getItem('userId') || 'anonymous',
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000,
          }
        );
      } catch (err) {
        ElMessage.error(`发送 Answer 失败: ${err.message}`);
      }
    };

    const handleWebRTCIceCandidate = async (candidate) => {
      if (peerConnection) {
        await peerConnection.addIceCandidate(candidate);
      }
    };

    const startWebRTCPolling = () => {
      pollingInterval = setInterval(async () => {
        try {
          const response = await axios.get('https://a.410121.xyz/api/webrtc/signaling', {
            params: {
              userId: localStorage.getItem('userId') || 'anonymous',
            },
            timeout: 5000,
          });
          if (response.data.code === 200 && response.data.signals) {
            response.data.signals.forEach((signal) => {
              if (signal.type === 'offer') {
                handleWebRTCOffer(signal.offer, signal.sourceId);
              } else if (signal.type === 'icecandidate') {
                handleWebRTCIceCandidate(signal.candidate);
              }
            });
          }
        } catch (err) {
          console.error('WebRTC 信令轮询失败:', err);
        }
      }, 10000); // 每 10 秒轮询
    };

    const loadSettings = () => {
      const history = JSON.parse(localStorage.getItem('transferHistory') || '[]');
      transferHistory.value = history;
    };

    const selectFile = (event) => {
      selectedFiles.value = Array.from(event.target.files);
      sendingStatus.value = `已选择 ${selectedFiles.value.length} 个文件`;
    };

    const showSendConfirmation = () => {
      if (!selectedFiles.value.length) {
        ElMessage.error('请选择文件');
        return;
      }
      if (fileExpiryHours.value < 1 || fileExpiryHours.value > 720) {
        ElMessage.error('文件有效期必须在 1 到 720 小时之间');
        return;
      }
      pickupCode.value = generatePickupCode();
      receiveLink.value = `https://a.410121.xyz/receive?code=${pickupCode.value}`;
      showSendDialog.value = true;
    };

    const generatePickupCode = () => {
      return Math.floor(100000 + Math.random() * 900000).toString();
    };

    const copyLink = () => {
      navigator.clipboard.writeText(receiveLink.value).then(() => {
        ElMessage.success('链接已复制');
      }).catch((err) => {
        ElMessage.error(`复制失败: ${err.message}`);
      });
    };

    const copyPickupCode = () => {
      navigator.clipboard.writeText(pickupCode.value).then(() => {
        ElMessage.success('取件码已复制');
      }).catch((err) => {
        ElMessage.error(`复制失败: ${err.message}`);
      });
    };

    const copyQRCode = () => {
      const canvas = document.querySelector('#qrcode-canvas');
      if (canvas && 'ClipboardItem' in window) {
        canvas.toBlob((blob) => {
          const item = new ClipboardItem({ 'image/png': blob });
          navigator.clipboard.write([item]).then(() => {
            ElMessage.success('二维码已复制');
          }).catch((err) => {
            ElMessage.error(`复制失败: ${err.message}`);
          });
        });
      } else {
        ElMessage.error('当前浏览器不支持复制图片，请手动保存二维码');
      }
    };

    const toggleQRCode = () => {
      showQR.value = !showQR.value;
      if (showQR.value) {
        QRCode.toCanvas(
          document.querySelector('#qrcode-canvas'),
          receiveLink.value,
          { width: 128 },
          (error) => {
            if (error) console.error('生成二维码失败:', error);
          }
        );
      }
    };

    const confirmSend = () => {
      closeSendDialog();
      sendFile();
    };

    const confirmDeviceSend = async () => {
      closeSendDialog();
      await sendFile();
    };

    const sendFile = async () => {
      sendingStatus.value = '正在发送...';
      const formData = new FormData();
      selectedFiles.value.forEach((file) => formData.append('files', file));
      formData.append('expiryHours', fileExpiryHours.value);
      formData.append('pickupCode', pickupCode.value);

      let url = 'https://a.410121.xyz/api/send';
      let headers = { 'Content-Type': 'multipart/form-data' };
      let token = localStorage.getItem('token');

      if (transferMode.value === 'sameNetwork' && selectedDevice.value) {
        try {
          if (transferProtocol.value === 'bluetooth') {
            const service = await selectedDevice.value.gatt.getService(
              '0000180d-0000-1000-8000-00805f9b34fb'
            );
            const characteristic = await service.getCharacteristic(
              '00002a37-0000-1000-8000-00805f9b34fb'
            );
            for (const file of selectedFiles.value) {
              const arrayBuffer = await file.arrayBuffer();
              await characteristic.writeValue(arrayBuffer);
            }
            sendingStatus.value = '蓝牙文件发送成功';
            ElMessage.success('发送成功');
          } else if (transferProtocol.value === 'nfc') {
            if ('NDEFWriter' in window) {
              const writer = new NDEFWriter();
              await writer.write({
                records: [{ recordType: 'text', data: pickupCode.value }],
              });
              sendingStatus.value = 'NFC 配对成功，等待接收方';
              ElMessage.success('NFC 配对成功');
            } else {
              throw new Error('浏览器不支持 NFC 写入');
            }
          } else if (transferProtocol.value === 'wifi') {
            const channel = peerConnection.createDataChannel('file-transfer');
            for (const file of selectedFiles.value) {
              const arrayBuffer = await file.arrayBuffer();
              channel.send(
                JSON.stringify({
                  name: file.name,
                  size: file.size,
                  data: arrayBuffer,
                })
              );
            }
            sendingStatus.value = 'Wi-Fi 文件发送成功';
            ElMessage.success('发送成功');
          }
        } catch (err) {
          sendingStatus.value = '发送失败';
          ElMessage.error(`发送失败: ${err.message}`);
        }
      } else {
        if (transferMode.value === 'sameNetwork') {
          if (!navigator.bluetooth && !('NDEFReader' in window)) {
            sendingStatus.value = '设备不支持蓝牙或 NFC，请使用 Wi-Fi';
            return;
          }
          headers['X-Transfer-Mode'] = 'sameNetwork';
        } else if (transferMode.value === 'loggedIn' && token) {
          headers.Authorization = `Bearer ${token}`;
          headers['X-Transfer-Mode'] = 'loggedIn';
        } else if (transferMode.value === 'anonymous') {
          headers['X-Transfer-Mode'] = 'anonymous';
        }

        try {
          const response = await axios.post(url, formData, { headers, timeout: 5000 });
          if (response.data.code === 200) {
            sendingStatus.value = `文件发送成功，取件码: ${pickupCode.value}`;
            ElMessage.success('文件发送成功');
            if (transferMode.value === 'loggedIn' && token) {
              const record = {
                timestamp: new Date().toISOString(),
                pickupCode: pickupCode.value,
                files: selectedFiles.value.map((f) => f.name),
                expiry: new Date(
                  Date.now() + fileExpiryHours.value * 60 * 60 * 1000
                ).toISOString(),
              };
              transferHistory.value.push(record);
              localStorage.setItem('transferHistory', JSON.stringify(transferHistory.value));
            }
          } else {
            throw new Error(response.data.message || '发送失败');
          }
        } catch (error) {
          sendingStatus.value = '发送失败';
          let message = error.response?.data?.message || error.message || '网络错误';
          if (error.code === 'ECONNABORTED') message = '请求超时';
          else if (error.response?.status === 401) {
            message = '认证失败，请重新登录';
            localStorage.removeItem('isLoggedIn');
            localStorage.removeItem('userId');
            localStorage.removeItem('username');
            localStorage.removeItem('token');
            window.location.href = '/login';
          }
          ElMessage.error(`发送失败: ${message}`);
        }
      }
    };

    const showReceiveInput = () => {
      showReceiveDialog.value = true;
    };

    const closeSendDialog = () => {
      showSendDialog.value = false;
      showQR.value = false;
    };

    const closeReceiveDialog = () => {
      showReceiveDialog.value = false;
    };

    const startReceiving = async () => {
      if (!inputPickupCode.value) {
        receivingStatus.value = '请输入取件码';
        return;
      }
      if (verifyExpiryHours.value < 1 || verifyExpiryHours.value > 720) {
        ElMessage.error('文件保存时间必须在 1 到 720 小时之间');
        return;
      }
      receivingStatus.value = '等待接收...';
      let url = 'https://a.410121.xyz/api/receive';
      let headers = {};

      if (transferMode.value === 'sameNetwork') {
        headers['X-Transfer-Mode'] = 'sameNetwork';
        headers['X-Pickup-Code'] = inputPickupCode.value;
      } else if (transferMode.value === 'loggedIn' && localStorage.getItem('token')) {
        headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
        headers['X-Transfer-Mode'] = 'loggedIn';
        headers['X-Pickup-Code'] = inputPickupCode.value;
      } else if (transferMode.value === 'anonymous') {
        headers['X-Pickup-Code'] = inputPickupCode.value;
        headers['X-Transfer-Mode'] = 'anonymous';
      }

      try {
        const response = await axios.get(url, {
          headers,
          params: { code: inputPickupCode.value, saveHours: verifyExpiryHours.value },
          timeout: 5000,
        });
        if (response.data.code === 200) {
          receivedFiles.value = response.data.data.files.map((file) => ({
            name: file.name,
            size: file.size,
            url: `https://a.410121.xyz/api/download/${file.id}`,
          }));
          receivingStatus.value = '接收成功';
          ElMessage.success('文件接收成功');
          if (transferMode.value === 'anonymous') {
            inputPickupCode.value = '';
          }
          if (receivedFiles.value.length) window.open(receivedFiles.value[0].url, '_blank');
        } else {
          throw new Error(response.data.message || '接收失败');
        }
      } catch (error) {
        receivingStatus.value = '接收失败';
        let message = error.response?.data?.message || error.message || '网络错误';
        if (error.code === 'ECONNABORTED') message = '请求超时';
        else if (error.response?.status === 401) {
          message = '认证失败，请重新登录';
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          localStorage.removeItem('token');
          window.location.href = '/login';
        } else if (error.response?.status === 404) {
          message = '取件码无效或已过期';
        }
        ElMessage.error(`接收失败: ${message}`);
      } finally {
        closeReceiveDialog();
      }
    };

    const startBackgroundScan = () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker
          .register('/service.js')
          .then(() => {
            console.log('Service Worker 注册成功');
          })
          .catch((err) => {
            console.error('Service Worker 注册失败:', err);
          });
      }
      if (window.isElectron || window.isJava) {
        console.log('启动原生后台扫描');
      }
    };

    const checkVersion = () => {
      VersionFlag.value = true;
    };

    const upgrade = () => {
      const link = document.createElement('a');
      link.href = versionFileUrl.value;
      link.download = 'uno.apk';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      ElMessage.success('下载完成，请手动安装新版本');
    };

    const closeVersionCover = () => {
      VersionFlag.value = false;
    };

    return {
      loading,
      error,
      transferMode,
      transferProtocol,
      sendingStatus,
      receivingStatus,
      receivedFiles,
      selectedFiles,
      pickupCode,
      inputPickupCode,
      transferHistory,
      fileExpiryHours,
      verifyExpiryHours,
      showSendDialog,
      showReceiveDialog,
      showQR,
      receiveLink,
      VersionFlag,
      VersionExplain,
      currentVersion,
      versionFileUrl,
      discoveredDevices,
      connecting,
      selectedDevice,
      connectDevice,
      sendOffer,
      sendIceCandidate,
      handleWebRTCOffer,
      sendAnswer,
      handleWebRTCIceCandidate,
      startWebRTCPolling,
      loadSettings,
      selectFile,
      showSendConfirmation,
      generatePickupCode,
      copyLink,
      copyPickupCode,
      copyQRCode,
      toggleQRCode,
      confirmSend,
      confirmDeviceSend,
      sendFile,
      showReceiveInput,
      closeSendDialog,
      closeReceiveDialog,
      startReceiving,
      startBackgroundScan,
      checkVersion,
      upgrade,
      closeVersionCover,
    };
  },
};
</script>

<style scoped>
.w {
  width: 1700px;
  margin: 0 auto;
}
img {
  width: 100%;
  height: 100%;
  display: block;
}
#home {
  width: 100%;
}
#contentBox {
  width: 100%;
  height: 100%;
  position: relative;
  background-repeat: no-repeat;
  overflow: auto;
  z-index: 0;
}
.registervideoContainer {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  margin-left: 0rem;
  width: 100%;
  height: 100%;
  position: fixed;
  z-index: -1;
  background-image: url(../assets/2.jpg);
  filter: blur(6px);
}
.mode-selection {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.mode-selection label {
  font-size: 16px;
  color: #ffffff;
}
.transfer-controls {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 0;
}
.send-section, .receive-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.time-selection {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
.time-selection label {
  font-size: 14px;
  color: #ffffff;
}
.time-input {
  width: 60px;
  padding: 5px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #2a2a2a;
  color: #ffffff;
}
.file-input {
  padding: 10px;
  background-color: #2a2a2a;
  color: #ffffff;
  border: none;
  border-radius: 5px;
}
.transfer-button {
  padding: 10px 20px;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  background-color: #1e90ff;
  color: #ffffff;
  cursor: pointer;
}
.transfer-button:hover {
  background-color: #1c86ee;
}
.status {
  font-size: 14px;
  color: #ffeb3b;
}
.received-files {
  margin-top: 20px;
  text-align: center;
}
.received-files ul {
  list-style: none;
  padding: 0;
}
.received-files li {
  padding: 10px;
  background-color: #2a2a2a;
  border-radius: 5px;
  margin: 5px 0;
}
.received-files a {
  color: #1e90ff;
  margin-left: 10px;
}
.loading, .error {
  margin-top: 20px;
  font-size: 16px;
}
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}
.modal-content {
  background: #2a2a2a;
  padding: 20px;
  border-radius: 5px;
  text-align: center;
  color: #ffffff;
}
.modal-button {
  padding: 10px 20px;
  margin: 10px;
  font-size: 16px;
  border: none;
  border-radius: 5px;
  background-color: #1e90ff;
  color: #ffffff;
  cursor: pointer;
}
.modal-button:hover {
  background-color: #1c86ee;
}
.modal-button.confirm {
  background-color: #ff4400;
}
.modal-button.confirm:hover {
  background-color: #e63900;
}
.qr-code {
  margin-top: 10px;
}
#manu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  max-width: 1700px;
  margin: 0 auto;
  background-color: #2a2a2a;
  z-index: 1000;
}
.airdrop-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.content-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 600px;
}
.logo-container {
  margin-top: 50px;
  text-align: center;
}
.logo {
  width: 100px;
  height: 100px;
}
.title {
  font-size: 2em;
  color: #1e90ff;
  margin: 10px 0;
}
.status-container {
  display: flex;
  align-items: center;
  gap: 10px;
}
.status-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.status-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.bubble-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center; /* 居中 */
  gap: 15px;
  padding: 10px;
  min-height: 200px; /* 确保容器有足够高度 */
}

.device-bubble {
  width: 120px;
  height: 120px;
  background: rgba(255, 255, 255, 0.7); /* 半透明白 */
  backdrop-filter: blur(5px); /* 模糊效果 */
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  position: relative;
}

.device-bubble:hover:not(.placeholder) {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.device-bubble.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.placeholder {
  background: rgba(240, 240, 240, 0.7); /* 灰色半透明 */
  backdrop-filter: blur(5px);
  color: #999;
  cursor: default;
}

.bubble-text {
  font-size: 14px;
  color: #333;
  padding: 5px;
  word-break: break-all;
  max-width: 100%;
}

.bubble-status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: #409eff;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 10px;
}
</style>